/**
 * DDSOptimiserPage — Dynamically Dimensioned Search auto-placement
 *
 * Tolson & Shoemaker 2007. Random perturbation of a subset of decision
 * variables each iteration, perturbation probability decreasing with
 * iteration. Accepts moves that improve the objective.
 *
 * Decision variables (5): moduleCount, cols, gapX, gapY, mountingHeight
 * Objective: minimise lifetime £/kg O₂ subject to constraints (coverage > 95%,
 *            AWWA M37 compliance OR override, bed shear < 2×τ_cr, AOTR/SOD > 1.2)
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Sparkles, ArrowLeft, Play, RefreshCcw, Info, TrendingDown } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  moduleAOTRkgh, moduleBedCoverageAreaM2, blowerKwForModule, bedShearPa,
} from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const RESERVOIR_AREA_M2 = 34_000;
const RESERVOIR_AREA_HA = 3.4;

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number; currentDoMgL?: number; alphaFactor?: number; betaFactor?: number; }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12, currentDoMgL: 2.0, alphaFactor: 0.85, betaFactor: 0.98 };

interface DecVars { moduleCount: number; cols: number; gapXM: number; gapYM: number; mountingHeightM: number; }
const VAR_BOUNDS: Record<keyof DecVars, [number, number]> = {
  moduleCount: [4, 40],
  cols: [1, 6],
  gapXM: [1.5, 8],
  gapYM: [1.5, 8],
  mountingHeightM: [0.30, 1.20],
};

interface Eval {
  v: DecVars;
  obj: number;          // lifetime cost-per-mg-O₂ + penalty
  capex: number;
  opex: number;
  AOTR_total: number;
  SOD_total: number;
  coveragePct: number;
  feasible: boolean;
  reason: string;
}

const SOD_kgM2D = 0.6;
const sodTotal_kgH = SOD_kgM2D * RESERVOIR_AREA_M2 / 24;

function evaluate(spec: ModSpec, v: DecVars): Eval {
  const cSpec = { ...spec, mountingHeightM: v.mountingHeightM };
  const dSpec = specFromLegacy(cSpec);
  const H_eff = Math.max(0.3, cSpec.waterDepthM - cSpec.mountingHeightM);
  const AOTR_mod = moduleAOTRkgh(dSpec, H_eff, cSpec.waterTempC, cSpec.alphaFactor ?? 0.85, cSpec.betaFactor ?? 0.98, cSpec.currentDoMgL ?? 2.0);
  const kw_mod = blowerKwForModule(dSpec, H_eff);
  const A_mod = moduleBedCoverageAreaM2(dSpec, H_eff) * 0.85;
  const tau = bedShearPa(dSpec, H_eff, cSpec.waterTempC);
  const AOTR_total = AOTR_mod * v.moduleCount;
  const coverageM2 = A_mod * v.moduleCount;
  const coveragePct = Math.min(100, (coverageM2 / RESERVOIR_AREA_M2) * 100);

  // Lifetime cost (20 yr, 4% discount)
  const capex = 1800 * v.moduleCount + 25000 + 1200 * v.moduleCount * kw_mod;
  const hours = 8760 * 0.55;
  const annual_opex = kw_mod * v.moduleCount * hours * 0.18 + 50 * v.moduleCount;
  const r = 0.04, n = 20;
  const opex_npv = annual_opex * (1 - Math.pow(1 + r, -n)) / r;
  const lifetime = capex + opex_npv;
  const O2_lifetime_kg = AOTR_total * hours * n;
  const cost_per_kg_O2 = lifetime / Math.max(1, O2_lifetime_kg);

  // Constraints
  let penalty = 0;
  let reason = '';
  if (coveragePct < 95) { penalty += (95 - coveragePct) * 5; reason += `cov ${coveragePct.toFixed(0)}%<95 `; }
  if (AOTR_total < sodTotal_kgH * 1.2) { penalty += (sodTotal_kgH * 1.2 - AOTR_total) * 80; reason += 'AOTR<1.2×SOD '; }
  if (tau > 0.4 * 2) { penalty += (tau - 0.4 * 2) * 200; reason += `shear ${tau.toFixed(2)}>0.8 Pa `; }
  if (v.moduleCount > 6 * RESERVOIR_AREA_HA) { penalty += (v.moduleCount - 6 * RESERVOIR_AREA_HA) * 0.5; reason += 'AWWA cap '; }
  const feasible = penalty === 0;

  return {
    v, obj: cost_per_kg_O2 + penalty,
    capex, opex: opex_npv, AOTR_total, SOD_total: sodTotal_kgH, coveragePct,
    feasible, reason: feasible ? 'feasible' : reason.trim(),
  };
}

function randInt(lo: number, hi: number): number {
  return Math.floor(lo + Math.random() * (hi - lo + 1));
}

function perturbDDS(current: DecVars, iter: number, maxIter: number): DecVars {
  const out: DecVars = { ...current };
  const p = 1 - Math.log(iter + 1) / Math.log(maxIter + 1);   // perturbation probability
  (Object.keys(VAR_BOUNDS) as (keyof DecVars)[]).forEach(k => {
    if (Math.random() < p) {
      const [lo, hi] = VAR_BOUNDS[k];
      const sigma = 0.2 * (hi - lo);
      const z = (Math.random() + Math.random() + Math.random() - 1.5) * 2 * sigma;
      let val = current[k] + z;
      if (k === 'moduleCount' || k === 'cols') val = Math.round(Math.max(lo, Math.min(hi, val)));
      else val = Math.max(lo, Math.min(hi, val));
      out[k] = val;
    }
  });
  return out;
}

export default function DDSOptimiserPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('dds_optimiser');
  const [spec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [maxIter, setMaxIter] = useState(200);
  const [history, setHistory] = useState<Eval[]>([]);
  const [best, setBest] = useState<Eval | null>(null);
  const [running, setRunning] = useState(false);

  const runOptimiser = () => {
    setRunning(true);
    // initial random sample
    let current: DecVars = {
      moduleCount: randInt(VAR_BOUNDS.moduleCount[0], VAR_BOUNDS.moduleCount[1]),
      cols: randInt(VAR_BOUNDS.cols[0], VAR_BOUNDS.cols[1]),
      gapXM: VAR_BOUNDS.gapXM[0] + Math.random() * (VAR_BOUNDS.gapXM[1] - VAR_BOUNDS.gapXM[0]),
      gapYM: VAR_BOUNDS.gapYM[0] + Math.random() * (VAR_BOUNDS.gapYM[1] - VAR_BOUNDS.gapYM[0]),
      mountingHeightM: VAR_BOUNDS.mountingHeightM[0] + Math.random() * (VAR_BOUNDS.mountingHeightM[1] - VAR_BOUNDS.mountingHeightM[0]),
    };
    let currentE = evaluate(spec, current);
    const hist: Eval[] = [currentE];
    let bestE = currentE;
    for (let i = 1; i <= maxIter; i++) {
      const candidate = perturbDDS(current, i, maxIter);
      const candE = evaluate(spec, candidate);
      if (candE.obj < currentE.obj) {
        current = candidate;
        currentE = candE;
        if (candE.obj < bestE.obj) bestE = candE;
      }
      hist.push(bestE);
    }
    setHistory(hist);
    setBest(bestE);
    setRunning(false);
  };

  // Render convergence curve
  const svgW = 720, svgH = 200;
  const padL = 60, padR = 20, padT = 15, padB = 25;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const yMax = useMemo(() => history.length ? Math.max(...history.map(h => h.obj)) : 100, [history]);
  const yMin = useMemo(() => history.length ? Math.min(...history.map(h => h.obj)) : 0, [history]);
  const x = (i: number) => padL + (i / Math.max(1, history.length - 1)) * plotW;
  const y = (v: number) => padT + plotH - ((v - yMin) / Math.max(0.001, yMax - yMin)) * plotH;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Sparkles}
        title="Module — DDS Pattern-Search Optimiser"
        subtitle="Dynamically Dimensioned Search (Tolson & Shoemaker 2007) for auto-placement"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('dds_optimiser')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="dds_optimiser" />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm items-end">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Iterations</label>
            <input type="number" step={50} min={50} max={1000} value={maxIter}
              onChange={e => setMaxIter(Number(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <button onClick={runOptimiser} disabled={running}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-500 disabled:opacity-50">
            <Play className="w-4 h-4" /> {running ? 'Running…' : 'Run optimiser'}
          </button>
          <button onClick={() => { setHistory([]); setBest(null); }}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700">
            <RefreshCcw className="w-4 h-4" /> Reset
          </button>
        </div>
      </div>

      {best && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
            <div className="text-xs uppercase tracking-wider text-emerald-300">Best £/kg O₂</div>
            <div className="text-2xl font-bold text-emerald-200 mt-1">£{best.obj.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{best.feasible ? 'feasible' : best.reason}</div>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400">Modules</div>
            <div className="text-2xl font-bold text-slate-100 mt-1">{best.v.moduleCount}</div>
            <div className="text-xs text-slate-500 mt-1">{best.v.cols} cols</div>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400">Gaps (m)</div>
            <div className="text-2xl font-bold text-slate-100 mt-1">{best.v.gapXM.toFixed(1)} × {best.v.gapYM.toFixed(1)}</div>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400">Mounting height</div>
            <div className="text-2xl font-bold text-slate-100 mt-1">{best.v.mountingHeightM.toFixed(2)} m</div>
            <div className="text-xs text-slate-500 mt-1">cov {best.coveragePct.toFixed(0)}%</div>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-emerald-400" /> Convergence
          </div>
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
            <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
            <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
            {[0, 0.25, 0.5, 0.75, 1].map(f => {
              const v = yMin + f * (yMax - yMin);
              return (
                <text key={f} x={padL - 4} y={padT + plotH - f * plotH + 3} textAnchor="end" fontSize={9} fill="#94a3b8">{v.toFixed(0)}</text>
              );
            })}
            <polyline fill="none" stroke="#22c55e" strokeWidth={2}
              points={history.map((h, i) => `${x(i)},${y(h.obj)}`).join(' ')} />
            <text x={padL + plotW / 2} y={padT + plotH + 20} textAnchor="middle" fontSize={10} fill="#94a3b8">iteration ({history.length - 1})</text>
          </svg>
          <div className="text-[10px] text-slate-500 mt-1">Best objective found at each iteration (lower = better)</div>
        </div>
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> How DDS works
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            DDS perturbs a randomly-selected subset of decision variables each iteration. The perturbation
            probability p(i) = 1 − log(i)/log(N) decreases with iteration, so early iterations explore
            broadly and later iterations refine locally. Each candidate is accepted only if it improves
            the objective (greedy hill-climb on a noisy surface).
          </p>
          <p>
            <strong className="text-emerald-300">Objective:</strong> lifetime £ / kg O₂ delivered plus
            penalty terms for constraint violations (coverage &lt; 95%, AOTR &lt; 1.2 × SOD, bed shear
            &gt; 2 × τ_cr, AWWA M37 cap). Penalty scaling means the optimiser self-finds feasible
            solutions without explicit constraint handling.
          </p>
          <p className="italic text-slate-500">Reference: Tolson & Shoemaker 2007 (DDS), Reed et al. 2013 (water-resources GA review).</p>
        </div>
      </div>
    </div>
  );
}
